import { cn } from '@/lib/cn';
import { isProductFeedbackAvailable } from '@/utils/heidiReach';
import { toast } from '@heidi/design-system/toast';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  clearPersistedVoiceWindowState,
  findLiveDraft,
  getPersistedComposerDraft,
  getToastedCreatedKey,
  voiceWorkspaceMessages as messages,
  paintVisibleInfoCards,
  setPersistedComposerDraft,
  setToastedCreatedKey,
  useVoiceWorkspace,
  VoiceAssistantComposer,
  VoiceTranscript,
  VoiceWindowCanvas,
  VoiceWindowHeader,
  VoiceWindowOverlayShell,
} from '@heidi/agents';
import { VoiceAssistantApprovalTopBar } from '../components/VoiceAssistantApprovalTopBar';
import { VoiceMicPermissionDenied } from '../components/VoiceMicPermissionDenied';
import { useVoiceAssistant } from '../hooks/useVoiceAssistant';
import { useVoicePaintShell } from '../paint';
import { useVoiceAssistantStore } from '../store/useVoiceAssistantStore';
import { useHasVoiceConversation } from './useHasVoiceConversation';
import { useWorkspaceCanvasActions } from './useWorkspaceCanvasActions';

export type VoiceWindowPresentation = 'overlay' | 'embedded';

export interface VoiceWindowProps {
  /** `overlay` = scrim modal; `embedded` = layout host slot (no scrim). */
  presentation?: VoiceWindowPresentation;
}

/** First-party Voice window — shared body; shell chrome differs by presentation. */
export function VoiceWindow({ presentation = 'overlay' }: VoiceWindowProps) {
  const intl = useIntl();
  const {
    workspace,
    infoCards,
    drafts,
    expandedDraftId,
    minimize,
    closeWorkspace,
    setContent,
    setExpandedDraftId,
    discardDraft,
  } = useVoiceWorkspace();
  const paintShell = useVoicePaintShell();
  const visibleInfoCards = paintVisibleInfoCards(infoCards, paintShell);
  const {
    connection,
    sessionState,
    greetingComplete,
    muted,
    assistantSpeaking,
    micStream,
    approval,
    toggleMute,
    sendText,
    resolveApproval,
  } = useVoiceAssistant();

  const [draft, setDraft] = useState(getPersistedComposerDraft);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    setPersistedComposerDraft(draft);
  }, [draft]);

  useEffect(() => {
    if (workspace) return;
    clearPersistedVoiceWindowState();
    setDraft('');
  }, [workspace]);

  useEffect(() => {
    return () => {
      if (useVoiceAssistantStore.getState().workspace) return;
      clearPersistedVoiceWindowState();
    };
  }, []);

  const isActive = connection === 'active';
  const liveDraft = findLiveDraft(drafts);
  // Created drafts are excluded from `findLiveDraft` — still need them for toast / Create actions mid-flight.
  const document = liveDraft
    ? {
        kind: 'draft-document' as const,
        documentKind: liveDraft.documentKind,
        title: liveDraft.title,
        subtitle: liveDraft.subtitle,
        sessionId: liveDraft.sessionId,
        content: liveDraft.content,
        status: liveDraft.status,
        steps: liveDraft.steps,
        createError: liveDraft.createError,
      }
    : null;
  const createdDraft = [...drafts]
    .reverse()
    .find((row) => !row.superseded && row.status === 'created');
  const isOverlay = presentation === 'overlay';
  const hasConversation = useHasVoiceConversation();
  const hasInfoCards = visibleInfoCards.length > 0;
  const hasInlineDrafts = drafts.length > 0;
  const showThread = hasConversation || hasInfoCards || hasInlineDrafts;
  const showEmptyState = !hasInlineDrafts && !showThread;

  const {
    quickStartBusy,
    followUpsEnabled,
    handleCreate,
    handleQuickStart,
    handleFollowUp,
  } = useWorkspaceCanvasActions({
    document,
    hasCanvasContent: hasInlineDrafts || hasInfoCards,
    connection,
    sendText,
  });

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Toast on Create success — window stays open (no auto-minimize) so the
  // clinician can keep working. Created drafts are excluded from findLiveDraft.
  useEffect(() => {
    if (!createdDraft) return;
    const createdKey = `${createdDraft.id}:${createdDraft.title}:${createdDraft.sessionId ?? ''}`;
    if (getToastedCreatedKey() === createdKey) return;
    setToastedCreatedKey(createdKey);
    toast({
      tone: 'success',
      title: intl.formatMessage(messages.created),
      description: createdDraft.title,
    });
  }, [createdDraft, intl]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        minimize();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [minimize]);

  const handleSendText = useCallback(() => {
    const trimmed = draft.trim();
    if (!trimmed || !isActive) return;
    sendText(trimmed);
    setDraft('');
  }, [draft, isActive, sendText]);

  const handleCloseWorkspace = useCallback(() => {
    clearPersistedVoiceWindowState();
    setDraft('');
    closeWorkspace();
  }, [closeWorkspace]);

  const handleToggleDraft = useCallback(
    (id: string) => {
      setExpandedDraftId(expandedDraftId === id ? null : id);
    },
    [expandedDraftId, setExpandedDraftId]
  );

  const handleDraftContentChange = useCallback(
    (id: string, content: string) => {
      if (liveDraft?.id !== id) return;
      setContent(content);
    },
    [liveDraft?.id, setContent]
  );

  const handleDraftCreate = useCallback(
    (id: string) => {
      if (liveDraft?.id !== id) return;
      handleCreate();
    },
    [liveDraft?.id, handleCreate]
  );

  if (!workspace || workspace.minimized) return null;

  const windowCard = (
    <section
      role="dialog"
      aria-modal={isOverlay ? 'false' : undefined}
      aria-label={intl.formatMessage(messages.overlayAriaLabel)}
      className={cn(
        'relative z-10 flex min-h-0 flex-1 flex-col overflow-hidden',
        'border-border bg-surface-page',
        isOverlay
          ? cn(
              'w-full max-w-6xl rounded-3xl border shadow-dialog',
              'transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none',
              entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
            )
          : 'size-full rounded-lg border shadow-sm'
      )}
      data-testid={isOverlay ? 'voice-workspace-overlay-card' : 'voice-window'}
      data-voice-presentation={presentation}
    >
      <VoiceWindowHeader
        connection={connection}
        sessionState={sessionState}
        greetingComplete={greetingComplete}
        onMinimize={minimize}
        onClose={handleCloseWorkspace}
        showFeedback={isProductFeedbackAvailable}
      />

      <div className="flex min-h-0 flex-1 flex-col">
        {showThread ? (
          <VoiceTranscript
            className="min-h-0 flex-1"
            infoCards={visibleInfoCards}
            drafts={drafts}
            expandedDraftId={expandedDraftId}
            onToggleDraft={handleToggleDraft}
            onDraftContentChange={handleDraftContentChange}
            onDraftCreate={handleDraftCreate}
            onDraftDiscard={discardDraft}
            followUpsEnabled={followUpsEnabled}
            onFollowUp={handleFollowUp}
          />
        ) : null}

        <VoiceWindowCanvas
          showEmptyState={showEmptyState}
          quickStartBusy={quickStartBusy}
          onQuickStart={handleQuickStart}
        />
      </div>

      <footer className="relative flex shrink-0 justify-center border-t border-border/60 px-4 py-3">
        {/* XXX: capped to the composer column; at full window width it read as a banner. */}
        <VoiceAssistantApprovalTopBar
          approval={approval}
          onResolve={resolveApproval}
          className="mx-auto max-w-xl"
        />
        <div className="flex w-full max-w-xl flex-col gap-2">
          <VoiceMicPermissionDenied />
          <VoiceAssistantComposer
            draft={draft}
            isActive={isActive}
            muted={muted}
            assistantSpeaking={assistantSpeaking}
            micStream={micStream}
            onDraftChange={setDraft}
            onSend={handleSendText}
            onToggleMute={toggleMute}
            embedded
          />
        </div>
      </footer>
    </section>
  );

  if (!isOverlay) {
    return windowCard;
  }

  return (
    <VoiceWindowOverlayShell
      entered={entered}
      minimizeLabel={intl.formatMessage(messages.minimize)}
      onMinimize={minimize}
    >
      {windowCard}
    </VoiceWindowOverlayShell>
  );
}
